import React, { createContext, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toPng, toSvg } from "html-to-image";
import {
    ReactFlow, ReactFlowProvider, Background, Controls, MiniMap, Panel, Handle, Position, NodeResizer,
    addEdge, applyNodeChanges, applyEdgeChanges, useReactFlow, useStore, ConnectionMode, MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import categorias, { infoServicio, slug } from "modAws/CatalogoAws";
import { leerCarpetaTf, refCarpeta } from "modAws/leerCarpeta";
import { GRUPOS } from "modAws/grupos";
import {
    esContenedor, anchoDe, mapa, absoluta, ancestros, esDescendiente, ordenar, destinoEn, hacerContenedor,
    reubicar, alternarColapso, plegarTodos, aEditor, aGrafo, construirVista, buscarNodos, alinear, ordenarAuto, TIPOS_CONEXION, colocarFragmento,
} from "modAws/grafoUtil";
import { revisar } from "modAws/revision";
import { generarTerraform } from "modAws/generarTf";
import { entradasDeCosto, resumirCosto } from "modAws/costo";
import { sobreCifrado } from "modAws/cifrar";
import FormCuenta from "modAws/FormCuenta";
import EditorPropiedades, { EditorMultiple } from "modAws/EditorPropiedades";
import { tipoRecurso, aplicarCambioProp, normalizarClave } from "modAws/propiedadesUtil";

export { GRUPOS };

const usd = (v) => v.toLocaleString("es-CL", { style: "currency", currency: "USD", maximumFractionDigits: 2 });

// "ResizeObserver loop completed with undelivered notifications" es un aviso benigno del navegador: React Flow cambia
// tamanos dentro del callback de un ResizeObserver al medir/redimensionar nodos. El overlay de errores de desarrollo (CRA)
// lo muestra como fatal y registra su detector antes que cualquier otro, asi que no se puede filtrar despues. Se evita en
// el origen: los callbacks se ejecutan en el siguiente frame (React Flow no depende de que sean sincronos).
if (typeof window !== "undefined" && window.ResizeObserver && !window.ResizeObserver.__diferido) {
    const RO = window.ResizeObserver;
    class ResizeObserverDiferido extends RO {
        constructor(callback) { super((entries, observer) => { window.requestAnimationFrame(() => callback(entries, observer)); }); }
    }
    ResizeObserverDiferido.__diferido = true;
    window.ResizeObserver = ResizeObserverDiferido;
}

// Devuelve el MISMO arreglo mientras no cambie lo que importa para los calculos pesados (id, padre, tipo y datos de cada objeto):
// mover, medir o redimensionar objetos no lo cambia. Sin esto, con 1.500 objetos la Revision se recalculaba en cada paso de
// medicion/arrastre (la carga tardaba más de un minuto).
const mismosNodos = (a, b) => a.length === b.length && a.every((n, i) => n.id === b[i].id && n.parentId === b[i].parentId && n.data === b[i].data && n.type === b[i].type && n.hidden === b[i].hidden);
const mismasAristas = (a, b) => a.length === b.length && a.every((e, i) => e.id === b[i].id && e.source === b[i].source && e.target === b[i].target);
const useEstable = (lista, igual) => {
    const ref = useRef(lista);
    if (ref.current !== lista && !igual(ref.current, lista)) ref.current = lista;
    return ref.current;
};
// Con este zoom o menos los objetos se dibujan simplificados (un cuadro de color): a esa escala no se lee nada y el DOM es mucho menor.
const ZOOM_SIMPLE = 0.3;
const useZoomBajo = () => useStore((s) => s.transform[2] < ZOOM_SIMPLE);

// Acciones que necesitan los nodos: marcar un punto de deshacer, plegar un contenedor, y si el diagrama es solo lectura.
const AccionesCtx = createContext({ marcar: () => {}, alternar: () => {}, ro: false });
// Portapapeles compartido entre diagramas de la misma sesion (copiar en uno, pegar en otro).
let PORTAPAPELES = null, PEGADOS = 0;
const MIME = "application/x-aws-item";

// Icono oficial de AWS (public/aws-icons/<id>.svg); si no existe, el icono genérico.
// Sin icono oficial en el paquete de AWS: ni se pide el archivo (el dev server lo mandaria al proxy y ensucia el log).
const SIN_ICONO = new Set(["datapipeline", "appstream", "generico", "capa"]);
const Icono = ({ id, carpeta = "", size = 48, color = "#666", fa = "fa fa-cube" }) => {
    const [falla, setFalla] = useState(false);
    if (falla || !id || SIN_ICONO.has(id)) return (
        <div style={{ width: size, height: size, borderRadius: 6, background: color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * .45 }}>
            <i className={fa} /></div>);
    return <img alt="" draggable={false} width={size} height={size} src={`${process.env.PUBLIC_URL}/aws-icons/${carpeta}${id}.svg`} onError={() => setFalla(true)} />;
};

const HANDLES = ["top", "bottom", "left", "right"].map(p => ({ id: p, pos: Position[p[0].toUpperCase() + p.slice(1)] }));
const Puntos = () => HANDLES.map(h => <Handle key={h.id} id={h.id} type="source" position={h.pos} style={{ opacity: .6 }} />);

const BotonPlegar = ({ id, colapsado }) => {
    const acc = useContext(AccionesCtx);
    return (
        <button type="button" className="nodrag nopan" title={colapsado ? "Desplegar" : "Plegar"} onClick={e => { e.stopPropagation(); acc.alternar(id); }}
            style={{ border: 0, background: "transparent", padding: "0 4px", cursor: "pointer", lineHeight: 1, color: "inherit" }}>
            <i className={`fa fa-chevron-${colapsado ? "right" : "down"}`} style={{ fontSize: 10 }} />
        </button>
    );
};
// Lo que un `terraform plan` hace con el objeto (data.cambio): se marca con color y una insignia.
const CAMBIOS = { crear: ["#198754", "+ nuevo"], modificar: ["#e0a800", "~ cambia"], eliminar: ["#dc3545", "− elimina"], reemplazar: ["#6f42c1", "± reemplaza"] };
const InsigniaCambio = ({ cambio }) => (CAMBIOS[cambio] ? (
    <span style={{ position: "absolute", top: -9, left: -2, background: CAMBIOS[cambio][0], color: "#fff", borderRadius: 8, fontSize: 9, fontWeight: 700, padding: "0 6px", zIndex: 5 }}>{CAMBIOS[cambio][1]}</span>) : null);
const InsigniaComentarios = ({ n }) => (n ? (
    <span title={`${n} comentario(s)`} style={{ position: "absolute", top: -8, right: -8, background: "#ffc107", color: "#000", borderRadius: 10, fontSize: 10, padding: "0 5px", zIndex: 5 }}>
        <i className="fa fa-comment" /> {n}</span>) : null);

const NodoAws = memo(({ id, data, selected }) => {
    const acc = useContext(AccionesCtx);
    const zoomBajo = useZoomBajo();
    const info = infoServicio(data.servicio);
    const color = info ? info.color : "#666";
    const titulo = data.label || (info && info.servicio.title);
    const nCom = (data.comentarios || []).length;
    if (zoomBajo && !data.contenedor) return (          // vista lejana: un cuadro del color de la categoria (y los puntos de conexion, que las flechas necesitan)
        <div style={{ position: "relative", width: 120, height: 70, borderRadius: 10, background: CAMBIOS[data.cambio] ? CAMBIOS[data.cambio][0] : color, opacity: data.cambio === "eliminar" ? .5 : .85 }}>
            <Puntos />
        </div>
    );
    if (data.contenedor) return (
        <div title={data.descripcion || ""} style={{ position: "relative", width: "100%", height: "100%", border: `2px solid ${CAMBIOS[data.cambio] ? CAMBIOS[data.cambio][0] : color}`, borderRadius: 6, background: color + "12", ...(data.cambio === "eliminar" ? { opacity: .6 } : {}) }}>
            <NodeResizer isVisible={selected && !data.colapsado && !acc.ro} minWidth={140} minHeight={90} onResizeStart={acc.marcar} />
            <Puntos />
            <InsigniaCambio cambio={data.cambio} />
            <InsigniaComentarios n={nCom} />
            <div className="d-flex align-items-center gap-1" style={{ padding: "2px 6px" }}>
                <BotonPlegar id={id} colapsado={!!data.colapsado} />
                <Icono id={data.servicio} size={26} color={color} fa={info ? info.servicio.icon : undefined} />
                <div style={{ lineHeight: 1.1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "var(--cui-body-color, #212529)" }}>{titulo}{data.colapsado && data.nOculto ? ` (${data.nOculto})` : ""}</div>
                    {data.objeto && <div style={{ fontSize: 10, opacity: .7 }}>{data.objeto}</div>}
                    {data.resumen && !data.colapsado && <div style={{ fontSize: 10, opacity: .85 }}>{data.resumen}</div>}
                </div>
            </div>
        </div>
    );
    return (
        <div title={data.descripcion || ""} style={{
            position: "relative", width: 120, textAlign: "center", padding: "6px 4px", borderRadius: 8, background: "var(--cui-body-bg, #fff)",
            border: `2px solid ${CAMBIOS[data.cambio] ? CAMBIOS[data.cambio][0] : selected ? "#0d6efd" : "transparent"}`, color: "var(--cui-body-color, #212529)",
            ...(selected && CAMBIOS[data.cambio] ? { boxShadow: "0 0 0 2px #0d6efd" } : {}), ...(data.cambio === "eliminar" ? { opacity: .6 } : {}),
        }}>
            <Puntos />
            <InsigniaCambio cambio={data.cambio} />
            <InsigniaComentarios n={nCom} />
            <div className="d-flex justify-content-center"><Icono id={data.servicio} size={48} color={color} fa={info ? info.servicio.icon : undefined} /></div>
            <div style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.2, marginTop: 2, ...(data.cambio === "eliminar" ? { textDecoration: "line-through" } : {}) }}>{titulo}</div>
            {data.objeto && <div style={{ fontSize: 10, opacity: .7 }}>{data.objeto}</div>}
            {data.resumen && <div style={{ fontSize: 10, opacity: .85 }}>{data.resumen}</div>}
        </div>
    );
});

const NodoGrupo = memo(({ id, data, selected }) => {
    const acc = useContext(AccionesCtx);
    const g = GRUPOS[data.tipo] || GRUPOS.generico;
    return (
        <div title={data.descripcion || ""} style={{ position: "relative", width: "100%", height: "100%", border: `2px ${g.dash && !CAMBIOS[data.cambio] ? "dashed" : "solid"} ${CAMBIOS[data.cambio] ? CAMBIOS[data.cambio][0] : g.color}`, borderRadius: 4, background: g.color + "10", ...(data.cambio === "eliminar" ? { opacity: .6 } : {}) }}>
            <NodeResizer isVisible={selected && !data.colapsado && !acc.ro} minWidth={120} minHeight={80} onResizeStart={acc.marcar} />
            <Puntos />
            <InsigniaCambio cambio={data.cambio} />
            <InsigniaComentarios n={(data.comentarios || []).length} />
            <div className="d-flex align-items-center gap-1" style={{ padding: "0 6px 0 2px", color: g.color }}>
                <BotonPlegar id={id} colapsado={!!data.colapsado} />
                <Icono id={GRUPOS[data.tipo] ? data.tipo : null} carpeta="grupos/" size={28} color={g.color} fa="fa fa-object-group" />
                <span style={{ fontSize: 12, fontWeight: 700 }}>{data.label || g.label}{data.colapsado && data.nOculto ? ` (${data.nOculto})` : ""}</span>
                {data.resumen && !data.colapsado && <span style={{ fontSize: 11, opacity: .8, color: "var(--cui-body-color, #212529)" }}> · {data.resumen}</span>}
            </div>
        </div>
    );
});
const nodeTypes = { aws: NodoAws, grupo: NodoGrupo };

let contador = 0;
const nuevoId = (pref) => `${pref}-${Date.now().toString(36)}${(contador++).toString(36)}`;

const Paleta = () => {
    const [q, setQ] = useState("");
    const { addNodes, screenToFlowPosition } = useReactFlow();
    const arrastrar = (e, item) => { e.dataTransfer.setData(MIME, JSON.stringify(item)); e.dataTransfer.effectAllowed = "move"; };
    const filtro = q.trim().toLowerCase();
    const cats = useMemo(() => categorias.map(c => ({
        ...c, servicios: c.servicios.filter(s => !filtro || s.title.toLowerCase().includes(filtro) || s.id.includes(filtro)),
    })).filter(c => c.servicios.length), [filtro]);
    return (
        <div style={{ width: 230, overflowY: "auto", borderRight: "1px solid var(--cui-border-color, #dee2e6)", padding: 8 }}>
            <input className="form-control form-control-sm mb-2" placeholder="Buscar servicio…" value={q} onChange={e => setQ(e.target.value)} />
            {!filtro && <>
                <div className="small fw-bold text-muted mb-1">Contenedores</div>
                <div className="d-flex flex-column gap-1 mb-2">{Object.entries(GRUPOS).map(([tipo, g]) => (
                    <div key={tipo} draggable onDragStart={e => arrastrar(e, { grupo: tipo })} title="Arrastra al lienzo"
                        style={{ cursor: "grab", fontSize: 12, padding: "1px 6px 1px 2px", borderLeft: `4px solid ${g.color}`, borderRadius: 3, background: "var(--cui-tertiary-bg, #f8f9fa)" }}>
                        <Icono id={tipo} carpeta="grupos/" size={20} color={g.color} fa="fa fa-object-group" /> <span className="ms-1">{g.label}</span>
                    </div>))}</div>
            </>}
            {cats.map(c => (
                <details key={c.title} open={!!filtro}>
                    <summary className="small fw-bold" style={{ cursor: "pointer" }}><i className={c.icon + " me-1"} />{c.title}</summary>
                    <div className="d-flex flex-column gap-1 my-1">{c.servicios.map(s => {
                        const info = infoServicio(s.id);
                        return (
                            <div key={s.id} draggable onDragStart={e => arrastrar(e, { servicio: s.id })} title="Arrastra al lienzo (o doble clic)"
                                onDoubleClick={() => addNodes({ id: nuevoId(s.id), type: "aws", position: screenToFlowPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 }), data: { servicio: s.id, label: s.title } })}
                                style={{ cursor: "grab", fontSize: 12, padding: "1px 6px 1px 2px", borderLeft: `4px solid ${info.color}`, borderRadius: 3, background: "var(--cui-tertiary-bg, #f8f9fa)" }}>
                                <Icono id={s.id} size={20} color={info.color} fa={s.icon} /> <span className="ms-1">{s.title}</span>
                            </div>);
                    })}</div>
                </details>
            ))}
        </div>
    );
};

// Notas del equipo sobre un objeto (se guardan dentro del propio diagrama).
const Comentarios = ({ lista, ro, onAgregar, onQuitar }) => {
    const [texto, setTexto] = useState("");
    const enviar = () => { if (texto.trim()) { onAgregar(texto.trim()); setTexto(""); } };
    return (
        <div className="mb-2">
            <label className="small fw-bold">Comentarios <span className="text-muted fw-normal">({lista.length})</span></label>
            {lista.map((c, i) => (
                <div key={i} className="border rounded p-1 mb-1 small" style={{ background: "var(--cui-tertiary-bg, #f8f9fa)" }}>
                    <div className="d-flex justify-content-between text-muted" style={{ fontSize: 10 }}>
                        <span>{c.autor} · {new Date(c.fecha).toLocaleString()}</span>
                        {!ro && <i className="fa fa-times" style={{ cursor: "pointer" }} title="Quitar" onClick={() => onQuitar(i)} />}
                    </div>
                    <div style={{ whiteSpace: "pre-wrap" }}>{c.texto}</div>
                </div>))}
            {!ro && <div className="d-flex gap-1">
                <input className="form-control form-control-sm" placeholder="Agregar comentario…" maxLength={300} value={texto}
                    onChange={e => setTexto(e.target.value)} onKeyDown={e => { if (e.key === "Enter") enviar(); }} />
                <button className="btn btn-outline-primary btn-sm" onClick={enviar}><i className="fa fa-paper-plane" /></button>
            </div>}
        </div>
    );
};

const Inspector = ({ nodo, multiples, onMultiple, arista, ro, tieneHijos, onNodo, onProps, onContenedor, onArista, onBorrar, onDuplicar, onComentar, onQuitarComentario }) => {
    if (multiples) return <EditorMultiple nodos={multiples} ro={ro} onAplicar={onMultiple} />;
    if (!nodo && !arista) return <div className="small text-muted p-2">Selecciona un elemento para ver y editar sus propiedades. Conecta servicios arrastrando desde sus puntos. Suelta un servicio sobre otro para que lo contenga. Ctrl+clic selecciona varios; Ctrl+C / Ctrl+V copian y pegan, Ctrl+Z deshace, Suprimir borra.</div>;
    if (arista) return (
        <div className="p-2">
            <label className="small fw-bold">Etiqueta de la conexión</label>
            <input className="form-control form-control-sm mb-2" disabled={ro} value={arista.label || ""} maxLength={100} onChange={e => onArista({ label: e.target.value })} />
            <label className="small fw-bold">Tipo de conexión</label>
            <select className="form-select form-select-sm mb-2" disabled={ro} value={arista.tipo || ""} onChange={e => onArista({ tipo: e.target.value || undefined })}>
                <option value="">(sin tipo)</option>{Object.entries(TIPOS_CONEXION).map(([k, t]) => <option key={k} value={k}>{t.nombre}</option>)}
            </select>
            {!ro && <button className="btn btn-outline-danger btn-sm" onClick={onBorrar}><i className="fa fa-trash me-1" />Eliminar conexión</button>}
        </div>
    );
    const info = infoServicio(nodo.data.servicio);
    return (
        <div className="p-2">
            <label className="small fw-bold">Nombre</label>
            <input className="form-control form-control-sm mb-2" disabled={ro} value={nodo.data.label || ""} maxLength={80} onChange={e => onNodo({ label: e.target.value })} />
            {nodo.type === "aws" && info && <>
                <label className="small fw-bold">Servicio</label>
                <div className="small mb-2">{info.servicio.title} <span className="text-muted">· {info.categoria.title}</span></div>
                <label className="small fw-bold">Objeto</label>
                <select className="form-select form-select-sm mb-2" disabled={ro} value={nodo.data.objeto || ""} onChange={e => onNodo({ objeto: e.target.value })}>
                    <option value="">—</option>{info.servicio.objetos.map(o => <option key={o}>{o}</option>)}
                </select>
                <div className="form-check mb-2">
                    <input id="chkCont" className="form-check-input" type="checkbox" checked={!!nodo.data.contenedor}
                        disabled={ro || (!!nodo.data.contenedor && tieneHijos)} onChange={e => onContenedor(e.target.checked)} />
                    <label htmlFor="chkCont" className="form-check-label small">Es contenedor (puede contener otros objetos)</label>
                    {nodo.data.contenedor && tieneHijos && <div className="small text-muted">Saca primero los objetos que contiene.</div>}
                </div>
            </>}
            {nodo.data.tf && <div className="small text-muted mb-1" style={{ wordBreak: "break-all" }}><i className="fa fa-file-code me-1" />{nodo.data.tf}</div>}
            {CAMBIOS[nodo.data.cambio] && (
                <div className="border rounded p-2 mb-2 small" style={{ borderColor: CAMBIOS[nodo.data.cambio][0] }}>
                    <span className="badge me-1" style={{ background: CAMBIOS[nodo.data.cambio][0], color: "#fff" }}>{CAMBIOS[nodo.data.cambio][1]}</span>
                    <b>{{ crear: "Terraform lo va a crear", modificar: "Terraform lo va a modificar", eliminar: "Terraform lo va a eliminar", reemplazar: "Terraform lo va a destruir y volver a crear" }[nodo.data.cambio]}</b>
                    {(nodo.data.cambios || []).length > 0 && <table className="table table-sm mb-0 mt-1" style={{ fontSize: 11 }}><tbody>
                        {nodo.data.cambios.map(([k, antes, despues]) => (
                            <tr key={k}><td className="text-muted" style={{ wordBreak: "break-word", width: "34%" }}>{k}</td>
                                <td style={{ wordBreak: "break-word", fontFamily: "monospace" }}><span className="text-danger">{antes}</span> → <span className="text-success">{despues}</span></td></tr>))}
                    </tbody></table>}
                </div>)}
            {(tipoRecurso(nodo) || (nodo.data.props || []).length > 0) && <EditorPropiedades key={nodo.id} nodo={nodo} ro={ro} onCambiar={onProps} />}
            <label className="small fw-bold">Descripción / cómo funciona</label>
            <textarea className="form-control form-control-sm mb-2" rows={4} disabled={ro} maxLength={500} value={nodo.data.descripcion || ""} onChange={e => onNodo({ descripcion: e.target.value })} />
            {nodo.type === "aws" && <>
                <label className="small fw-bold">Costo mensual manual (USD)</label>
                <input type="number" min="0" step="any" className="form-control form-control-sm mb-2" disabled={ro} placeholder="vacío = se calcula solo si se puede"
                    value={nodo.data.costoMensual === undefined ? "" : nodo.data.costoMensual}
                    onChange={e => onNodo({ costoMensual: e.target.value === "" ? undefined : Math.max(0, Number(e.target.value)) })} />
            </>}
            <Comentarios key={`c${nodo.id}`} lista={nodo.data.comentarios || []} ro={ro} onAgregar={onComentar} onQuitar={onQuitarComentario} />
            {!ro && <div className="d-flex gap-2">
                <button className="btn btn-outline-secondary btn-sm" title="Ctrl+D" onClick={onDuplicar}><i className="fa fa-clone me-1" />Duplicar</button>
                <button className="btn btn-outline-danger btn-sm" onClick={onBorrar}><i className="fa fa-trash me-1" />Eliminar{esContenedor(nodo) ? " (con su contenido)" : ""}</button>
            </div>}
        </div>
    );
};

// Compara el diagrama con la cuenta de AWS real (credenciales temporales de solo lectura, cifradas, sin guardar).
const PanelDeriva = ({ onComparar, onIr, onMarcar, onQuitarMarcas, onAgregarFragmento, ro }) => {
    const [abierto, setAbierto] = useState(false);
    const [trabajando, setTrabajando] = useState(false);
    const [res, setRes] = useState(null);
    const [error, setError] = useState("");
    const enviar = async (datos) => {
        setTrabajando(true); setError(""); setRes(null);
        try {
            const d = await onComparar(datos);
            if (d && d.ok && d.rows && d.rows[0]) setRes(d.rows[0]); else setError((d && d.msg) || "No se pudo comparar con la cuenta.");
        } catch (err) { setError("No se pudo comparar con la cuenta."); }
        setTrabajando(false);
    };
    return (
        <div className="p-2 border-top small">
            <div className="d-flex justify-content-between align-items-center">
                <b>Comparar con la cuenta AWS</b>
                <button className="btn btn-outline-secondary btn-sm py-0" onClick={() => setAbierto(a => !a)}>{abierto ? "Ocultar" : "Abrir"}</button>
            </div>
            {abierto && <div className="mt-2">
                <div className="text-muted mb-2">Lee la cuenta en solo lectura y muestra qué hay en AWS que no está dibujado y qué está dibujado pero ya no existe. Las credenciales se cifran y no se guardan.</div>
                <FormCuenta compacto onEnviar={enviar} trabajando={trabajando} textoBoton="Comparar" />
            </div>}
            {error && <div className="text-danger mt-2">{error}</div>}
            {res && <div className="mt-2">
                <div>Cuenta <b>{res.cuenta}</b>: <b>{res.coinciden}</b> coinciden{res.noComparables ? ` · ${res.noComparables} sin nombre real para comparar` : ""}.</div>
                <div className="fw-bold mt-2 text-warning">Solo en AWS ({res.soloEnAws.length})</div>
                {!res.soloEnAws.length && <div className="text-muted">Nada nuevo.</div>}
                {res.soloEnAws.map((x, i) => <div key={i} className="text-muted">+ {x.etiqueta} <span style={{ fontSize: 10 }}>{x.objeto || x.tipo}</span></div>)}
                {!ro && res.fragmento && <button className="btn btn-outline-success btn-sm py-0 mt-1" onClick={() => onAgregarFragmento(res.fragmento.grafo)}><i className="fa fa-plus me-1" />Agregar estos {res.soloEnAws.length} al diagrama</button>}
                {res.soloEnAws.length > 0 && !res.fragmento && <div className="text-muted" style={{ fontSize: 10 }}>Son demasiados para agregarlos de una vez: importa la cuenta como diagrama nuevo.</div>}
                <div className="fw-bold mt-2 text-danger">Solo en el diagrama ({res.soloEnDiagrama.length})</div>
                {!res.soloEnDiagrama.length && <div className="text-muted">Todo lo dibujado existe.</div>}
                {res.soloEnDiagrama.length > 0 && <div className="d-flex gap-1 mb-1">
                    <button className="btn btn-outline-danger btn-sm py-0" onClick={() => onMarcar(res.soloEnDiagrama.map(x => x.nodo))}><i className="fa fa-highlighter me-1" />Marcar en el diagrama</button>
                    <button className="btn btn-outline-secondary btn-sm py-0" onClick={onQuitarMarcas}>Quitar marcas</button></div>}
                {res.soloEnDiagrama.map(x => <div key={x.nodo} style={{ cursor: "pointer" }} onClick={() => onIr(x.nodo)}>− {x.etiqueta} <span className="text-muted" style={{ fontSize: 10 }}>{x.tipo}</span></div>)}
                <div className="text-muted mt-2">Se compara por nombre (y CIDR en VPC/subredes). No se comparan discos, NAT, gateways ni reglas, porque AWS les asigna ids al azar.</div>
                {res.avisos.filter(a => /^Sin permiso|No se pudo leer/.test(a)).map((a, i) => <div key={i} className="text-warning">{a}</div>)}
            </div>}
        </div>
    );
};

const COLOR_NIVEL = { alto: "danger", medio: "warning", info: "info" };
const PanelRevision = ({ hallazgos, onIr, onCorregir, ro }) => (
    <div className="p-2">
        {!hallazgos.length && <div className="small text-success"><i className="fa fa-check-circle me-1" />Sin hallazgos con las reglas actuales.</div>}
        {!ro && hallazgos.some(h => h.arreglo) && <button className="btn btn-outline-success btn-sm mb-2" onClick={() => onCorregir(hallazgos)}><i className="fa fa-wrench me-1" />Corregir los {hallazgos.filter(h => h.arreglo).length} que tienen arreglo</button>}
        {hallazgos.map((h, i) => (
            <div key={i} className={`border border-${COLOR_NIVEL[h.nivel]} rounded p-1 mb-2 small`} style={{ cursor: "pointer" }} onClick={() => onIr(h.nodo)}>
                <span className={`badge text-bg-${COLOR_NIVEL[h.nivel]} me-1`}>{h.nivel}</span><b>{h.titulo}</b>
                <div className="text-muted">{h.detalle}</div>
                {h.arreglo && !ro && <button className="btn btn-outline-success btn-sm py-0 mt-1" style={{ fontSize: 11 }} title={h.arreglo.nota || ""}
                    onClick={e => { e.stopPropagation(); onCorregir([h]); }}><i className="fa fa-wrench me-1" />Corregir: {h.arreglo.clave} → {h.arreglo.valor}</button>}
            </div>))}
        <div className="small text-muted">Revisa lo que el diagrama sabe (contenedores, conexiones y propiedades de Terraform). No reemplaza una auditoría de seguridad.</div>
    </div>
);

// Fotos anteriores del diagrama: se puede traer una a la pantalla (sin guardar) para comparar o volver atras.
const PanelHistorial = ({ cargar, onRestaurar }) => {
    const [filas, setFilas] = useState(null);
    useEffect(() => { let vivo = true; cargar().then(d => { if (vivo) setFilas((d && d.rows) || []); }).catch(() => { if (vivo) setFilas([]); }); return () => { vivo = false; }; }, [cargar]);
    if (!filas) return <div className="p-2 small text-muted">Cargando…</div>;
    return (
        <div className="p-2">
            {!filas.length && <div className="small text-muted">Aún no hay versiones anteriores. Se guarda una foto al guardar (como máximo cada 5 minutos, las últimas 20).</div>}
            {filas.map(v => (
                <div key={v.id} className="border rounded p-1 mb-2 small">
                    <div><b>v{v.version}</b> · {new Date(v.created_at).toLocaleString()}</div>
                    <div className="text-muted">{v.modificado_por || "—"} · {v.nodos} objetos</div>
                    <button className="btn btn-outline-primary btn-sm mt-1" onClick={() => onRestaurar(v)}><i className="fa fa-history me-1" />Traer a la pantalla</button>
                </div>))}
            <div className="small text-muted">«Traer a la pantalla» reemplaza lo que ves por esa versión sin guardar: puedes deshacer con Ctrl+Z o guardar para confirmarla.</div>
        </div>
    );
};

// Estimacion de costo mensual con los precios de la empresa (se editan aqui y quedan guardados para todo el equipo).
const PanelCosto = ({ nodes, cargarPrecios, onGuardar, onEliminar }) => {
    const [precios, setPrecios] = useState(null);
    const [msg, setMsg] = useState("");
    useEffect(() => {
        let vivo = true;
        cargarPrecios().then(d => { if (vivo) setPrecios(Object.fromEntries(((d && d.rows) || []).map(r => [r.clave, Number(r.precio)]))); }).catch(() => { if (vivo) setPrecios({}); });
        return () => { vivo = false; };
    }, [cargarPrecios]);
    const entradas = useMemo(() => entradasDeCosto(nodes), [nodes]);
    const r = useMemo(() => (precios ? resumirCosto(entradas, precios, nodes) : null), [entradas, precios, nodes]);
    if (!r) return <div className="p-2 small text-muted">Cargando…</div>;
    const guardar = async (f, texto) => {
        const t = texto.trim().replace(",", ".");
        if (t === "") { if (f.precio !== undefined) { setPrecios(p => { const c = { ...p }; delete c[f.clave]; return c; }); await onEliminar(f.clave); } return; }
        const v = Number(t);
        if (!Number.isFinite(v) || v < 0) return setMsg("El precio debe ser un número mayor o igual a 0.");
        setMsg(""); setPrecios(p => ({ ...p, [f.clave]: v }));
        const d = await onGuardar(f.clave, v);
        if (!d || !d.ok) setMsg((d && d.msg) || "No se pudo guardar el precio.");
    };
    return (
        <div className="p-2 small">
            {!r.filas.length && <div className="text-muted mb-2">No hay objetos con costo deducible (instancias EC2/RDS, NAT, balanceadores, discos EBS, ElastiCache). Para cualquier otro, anota su costo mensual en sus propiedades.</div>}
            {r.filas.map(f => (
                <div key={f.clave} className="border rounded p-1 mb-2">
                    <div className="d-flex justify-content-between"><b>{f.titulo}</b><span>{f.precio === undefined ? <span className="text-warning">sin precio</span> : usd(f.subtotal)}</span></div>
                    <div className="d-flex align-items-center gap-1 text-muted">
                        <span>{f.cantidad} {f.unidad === "GB-mes" ? "GB" : f.nodos > 1 || f.cantidad > 1 ? "unid." : "unid."} ×</span>
                        {f.fijo ? <span>{usd(f.precio)} / mes (manual)</span> : <>
                            <input className="form-control form-control-sm" style={{ width: 90 }} inputMode="decimal" placeholder="USD" defaultValue={f.precio === undefined ? "" : f.precio}
                                key={`${f.clave}-${f.precio}`} onBlur={e => guardar(f, e.target.value)} onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }} />
                            <span>/ {f.unidad}</span></>}
                    </div>
                </div>))}
            {msg && <div className="text-danger mb-2">{msg}</div>}
            <div className="border-top pt-2 d-flex justify-content-between"><b>Total mensual</b><b>{usd(r.total)}</b></div>
            {r.sinPrecio > 0 && <div className="text-warning">Parcial: {r.sinPrecio} objeto(s) sin precio.</div>}
            {r.porCapa.length > 0 && <div className="mt-2"><div className="fw-bold">Por capa</div>
                {r.porCapa.map(c => <div key={c.capa} className="d-flex justify-content-between"><span>{c.capa}{c.sinPrecio ? " *" : ""}</span><span>{usd(c.total)}</span></div>)}
                {r.porCapa.some(c => c.sinPrecio) && <div className="text-muted">* incluye objetos sin precio</div>}</div>}
            <div className="text-muted mt-3">Los precios los define tu empresa (USD por mes; los discos por GB-mes) y los comparte todo el equipo. El cálculo es precio × cantidad, sin tráfico, S3, Lambda ni descuentos. Para cifras oficiales usa calculator.aws.</div>
        </div>
    );
};

// Enlaces publicos de solo lectura al diagrama GUARDADO (lo que se vea ahi sale de la ultima version guardada).
const PanelCompartir = ({ idDiagrama, onCrear, onListar, onRevocar }) => {
    const [filas, setFilas] = useState(null);
    const [dias, setDias] = useState(7);
    const [conProps, setConProps] = useState(false);
    const [nuevo, setNuevo] = useState(null);
    const [msg, setMsg] = useState("");
    const [trabajando, setTrabajando] = useState(false);
    const cargar = useCallback(() => onListar(idDiagrama).then(d => setFilas((d && d.rows) || [])).catch(() => setFilas([])), [onListar, idDiagrama]);
    useEffect(() => { cargar(); }, [cargar]);
    const crear = async () => {
        setTrabajando(true); setMsg("");
        const d = await onCrear(idDiagrama, dias, conProps);
        setTrabajando(false);
        if (d && d.ok && d.rows && d.rows[0]) { setNuevo({ url: `${window.location.origin}/Aws/Compartido/${d.rows[0].token}`, expira: d.rows[0].expira }); cargar(); }
        else setMsg((d && d.msg) || "No se pudo crear el enlace.");
    };
    const revocar = async (f) => { if (window.confirm("¿Revocar este enlace? Dejará de funcionar de inmediato.")) { await onRevocar(f.id); cargar(); } };
    const copiar = () => {
        if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(nuevo.url).catch(() => window.prompt("Copia el enlace:", nuevo.url));
        else window.prompt("Copia el enlace:", nuevo.url);
    };
    const estado = (f) => (f.revocado ? ["revocado", "secondary"] : new Date(f.expira) < new Date() ? ["vencido", "secondary"] : ["activo", "success"]);
    return (
        <div className="p-2 small">
            <div className="fw-bold mb-1">Nuevo enlace de solo lectura</div>
            <div className="d-flex gap-1 mb-1">
                <select className="form-select form-select-sm" value={dias} onChange={e => setDias(Number(e.target.value))}>
                    <option value={1}>Vence en 1 día</option><option value={7}>Vence en 7 días</option><option value={30}>Vence en 30 días</option><option value={90}>Vence en 90 días</option>
                </select>
                <button className="btn btn-primary btn-sm" disabled={trabajando} onClick={crear}>Crear</button>
            </div>
            <div className="form-check mb-2">
                <input id="chkProps" className="form-check-input" type="checkbox" checked={conProps} onChange={e => setConProps(e.target.checked)} />
                <label htmlFor="chkProps" className="form-check-label">Incluir propiedades (CIDR, reglas de red…)</label>
            </div>
            <div className="text-muted mb-2">Cualquiera con el enlace verá el diagrama sin iniciar sesión. Nunca se muestran los comentarios{conProps ? "" : " ni las propiedades"}.</div>
            {msg && <div className="text-danger mb-2">{msg}</div>}
            {nuevo && <div className="border border-success rounded p-1 mb-3">
                <div className="fw-bold text-success">Enlace creado — cópialo ahora, no se podrá volver a ver</div>
                <input className="form-control form-control-sm my-1" readOnly value={nuevo.url} onFocus={e => e.target.select()} />
                <div className="d-flex justify-content-between"><span className="text-muted">Vence {new Date(nuevo.expira).toLocaleString()}</span><button className="btn btn-outline-primary btn-sm" onClick={copiar}><i className="fa fa-copy me-1" />Copiar</button></div>
            </div>}
            <div className="fw-bold mb-1">Enlaces de este diagrama</div>
            {filas === null && <div className="text-muted">Cargando…</div>}
            {filas && !filas.length && <div className="text-muted">Aún no hay enlaces.</div>}
            {(filas || []).map(f => { const [txt, color] = estado(f); return (
                <div key={f.id} className="border rounded p-1 mb-1">
                    <div className="d-flex justify-content-between"><span className={`badge text-bg-${color}`}>{txt}</span>{!f.revocado && new Date(f.expira) > new Date() && <button className="btn btn-outline-danger btn-sm py-0" onClick={() => revocar(f)}>Revocar</button>}</div>
                    <div className="text-muted">Vence {new Date(f.expira).toLocaleString()} · {f.creado_por}</div>
                    <div className="text-muted">{f.accesos} visita(s){f.ultimo_acceso ? ` · última ${new Date(f.ultimo_acceso).toLocaleString()}` : ""}{f.incluir_props ? " · con propiedades" : ""}</div>
                </div>); })}
        </div>
    );
};

const Editor = ({ idDiagrama, nombreInicial, grafoInicial, guardando, onGuardar, onDirty, onAgregarCarpeta, onAvisos, onVersiones, onVersionGet, onGuardarPlantilla, onPrecios, onPrecioGuardar, onPrecioEli, onCompartirCrear, onCompartirList, onCompartirRevocar, onClavePublica, onCompararCuenta, onAgregarCuenta }) => {
    const [verPaleta, setVerPaleta] = useState(true);
    const [verInsp, setVerInsp] = useState(true);
    const [panel, setPanel] = useState("props");
    const [resaltar, setResaltar] = useState(true);
    const [ro, setRo] = useState(false);
    const [busqueda, setBusqueda] = useState("");
    const [leyenda, setLeyenda] = useState(false);
    const [marcas, setMarcas] = useState({});          // id -> "falta": objetos que ya no existen en AWS (solo se ven, no se guardan)
    const [panelCuenta, setPanelCuenta] = useState(false);
    const [agregandoCuenta, setAgregandoCuenta] = useState(false);
    const ini = useMemo(() => aEditor(grafoInicial), [grafoInicial]);
    const [nodes, setNodes] = useState(ini.nodes);
    const [edges, setEdges] = useState(ini.edges);
    const [nombre, setNombre] = useState(nombreInicial);
    const nodosEstables = useEstable(nodes, mismosNodos), aristasEstables = useEstable(edges, mismasAristas);      // para los calculos pesados
    const { screenToFlowPosition, fitView, getViewport, setViewport } = useReactFlow();
    const refNodes = useRef(nodes); refNodes.current = nodes;
    const refEdges = useRef(edges); refEdges.current = edges;
    const refLienzo = useRef(null);
    const acciones = useRef({});
    const [exportando, setExportando] = useState(false);
    const autor = (() => { try { return localStorage.getItem("login_email") || "usuario"; } catch (e) { return "usuario"; } })();

    // Historial (deshacer/rehacer): fotos de {nodes, edges} tomadas ANTES de cada cambio.
    const pasado = useRef([]), futuro = useRef([]), ultimoAgrupado = useRef({ clave: "", t: 0 });
    const [, refrescar] = useState(0);
    const marcar = useCallback(() => {
        pasado.current.push({ nodes: refNodes.current, edges: refEdges.current });
        if (pasado.current.length > 100) pasado.current.shift();
        futuro.current = [];
        refrescar(n => n + 1);
    }, []);
    // Ediciones seguidas del mismo campo (escribir en el inspector) cuentan como un solo paso.
    const marcarAgrupado = (clave) => {
        const u = ultimoAgrupado.current, ahora = Date.now();
        if (u.clave !== clave || ahora - u.t > 1500) marcar();
        ultimoAgrupado.current = { clave, t: ahora };
    };
    const deshacer = () => {
        const h = pasado.current.pop(); if (!h) return;
        futuro.current.push({ nodes: refNodes.current, edges: refEdges.current });
        setNodes(h.nodes); setEdges(h.edges); onDirty(); refrescar(n => n + 1);
    };
    const rehacer = () => {
        const h = futuro.current.pop(); if (!h) return;
        pasado.current.push({ nodes: refNodes.current, edges: refEdges.current });
        setNodes(h.nodes); setEdges(h.edges); onDirty(); refrescar(n => n + 1);
    };

    useEffect(() => { setTimeout(() => fitView({ padding: .2 }), 50); }, [fitView]);

    const onNodesChange = useCallback((c) => { setNodes(ns => applyNodeChanges(c, ns)); if (c.some(x => x.type !== "select" && x.type !== "dimensions")) onDirty(); }, [onDirty]);
    const onEdgesChange = useCallback((c) => { setEdges(es => applyEdgeChanges(c, es)); if (c.some(x => x.type !== "select")) onDirty(); }, [onDirty]);
    const onConnect = useCallback((p) => { marcar(); setEdges(es => addEdge({ ...p, id: nuevoId("e") }, es)); onDirty(); }, [onDirty, marcar]);

    // Mete `id` dentro de `destino` (convirtiendolo en contenedor si hace falta).
    const anidar = (ns, id, destino) => reubicar(destino && !esContenedor(destino) ? hacerContenedor(ns, destino.id) : ns, id, destino ? destino.id : null);

    const onDrop = (e) => {
        e.preventDefault();
        const raw = e.dataTransfer.getData(MIME);
        if (!raw || ro) return;
        const item = JSON.parse(raw), punto = screenToFlowPosition({ x: e.clientX, y: e.clientY });
        const base = item.grupo
            ? { id: nuevoId("g"), type: "grupo", data: { tipo: item.grupo, label: GRUPOS[item.grupo].label }, width: GRUPOS[item.grupo].w, height: GRUPOS[item.grupo].h }
            : { id: nuevoId(item.servicio), type: "aws", data: { servicio: item.servicio, label: infoServicio(item.servicio).servicio.title } };
        marcar();
        const ns = refNodes.current, destino = destinoEn(ns, punto, null);
        // Se crea en la raiz con posicion absoluta y luego se anida (conserva la posicion en pantalla).
        const conNuevo = ordenar([...ns, { ...base, position: { x: punto.x - 60, y: punto.y - 40 } }]);
        setNodes(destino ? anidar(conNuevo, base.id, destino) : conNuevo);
        onDirty();
    };
    // Al soltar un nodo arrastrado sobre otro (o fuera de su contenedor) cambia de padre.
    const onNodeDragStop = (_e, nodo) => {
        const ns = refNodes.current, porId = mapa(ns), actual = porId[nodo.id];
        if (!actual) return;
        const a = absoluta(actual, porId);
        const punto = esContenedor(actual) ? { x: a.x + 20, y: a.y + 20 } : { x: a.x + anchoDe(actual) / 2, y: a.y + (actual.height || 96) / 2 };
        const destino = destinoEn(ns, punto, nodo.id);
        if ((destino ? destino.id : null) !== (actual.parentId || null)) { setNodes(anidar(ns, nodo.id, destino)); onDirty(); }
    };

    const selNodos = nodes.filter(n => n.selected), selNodo = selNodos[0], selArista = !selNodo && edges.find(e => e.selected);
    const tieneHijos = !!selNodo && nodes.some(n => n.parentId === selNodo.id);
    const cambiarNodo = (d) => { marcarAgrupado(`n${selNodo.id}${Object.keys(d)[0]}`); setNodes(ns => ns.map(n => (n.id === selNodo.id ? { ...n, data: { ...n.data, ...d } } : n))); onDirty(); };
    // props editadas desde el panel: se guardan en el objeto junto con cuales se tocaron y el resumen recalculado (si el tipo lo tiene)
    const cambiarProps = (props, editadas, resumen) => cambiarNodo({ props, propsEditadas: editadas, ...(resumen !== undefined ? { resumen } : {}) });
    // la misma propiedad en varios objetos (valor null = quitarla); cada objeto conserva la forma de clave que ya tenia
    const cambiarMuchos = (clave, valor) => {
        const ids = new Set(selNodos.map(n => n.id));
        marcar();
        setNodes(ns => ns.map(n => {
            if (!ids.has(n.id)) return n;
            const existente = (n.data.props || []).find(([k]) => normalizarClave(k) === normalizarClave(clave));
            return aplicarCambioProp(n, existente ? existente[0] : clave, valor);
        }));
        onDirty();
    };
    // "Corregir" de la Revision: aplica el valor recomendado a la propiedad (con aviso de sus consecuencias y deshacer)
    const corregir = (hallazgos) => {
        const lista = hallazgos.filter(h => h.arreglo);
        if (!lista.length) return;
        const notas = [...new Set(lista.map(h => h.arreglo.nota).filter(Boolean))];
        const txt = `Se ${lista.length === 1 ? "aplicará 1 corrección" : `aplicarán ${lista.length} correcciones`} en el diagrama (puedes deshacer con Ctrl+Z).` +
            (notas.length ? `\n\nTen en cuenta:\n• ${notas.join("\n• ")}` : "") + "\n\nEsto cambia el diagrama; no modifica tu infraestructura ni tu Terraform.";
        if (!window.confirm(txt)) return;
        marcar();
        setNodes(ns => ns.map(n => lista.filter(h => h.nodo === n.id).reduce((acc, h) => aplicarCambioProp(acc, h.arreglo.clave, h.arreglo.valor), n)));
        onDirty();
    };
    const cambiarContenedor = (si) => {
        marcar();
        setNodes(ns => (si ? hacerContenedor(ns, selNodo.id)
            : ns.map(n => { if (n.id !== selNodo.id) return n; const { width, height, ...r } = n; return { ...r, data: { ...n.data, contenedor: false } }; })));
        onDirty();
    };
    const cambiarArista = (d) => { marcarAgrupado(`a${selArista.id}`); setEdges(es => es.map(x => (x.id === selArista.id ? { ...x, ...d } : x))); onDirty(); };
    const comentar = (texto) => { marcar(); cambiarNodo({ comentarios: [...(selNodo.data.comentarios || []), { autor, fecha: new Date().toISOString(), texto }] }); };
    const quitarComentario = (i) => { marcar(); cambiarNodo({ comentarios: (selNodo.data.comentarios || []).filter((_, k) => k !== i) }); };
    const borrarSel = () => {
        if (ro) return;
        if (selNodo || selArista) marcar();
        if (selNodo) {
            const porId = mapa(nodes), quitar = new Set([selNodo.id]);
            nodes.forEach(n => { if (esDescendiente(n, selNodo.id, porId)) quitar.add(n.id); });
            setNodes(ns => ns.filter(n => !quitar.has(n.id))); setEdges(es => es.filter(x => !quitar.has(x.source) && !quitar.has(x.target)));
        } else if (selArista) setEdges(es => es.filter(x => x.id !== selArista.id));
        onDirty();
    };

    // ---- plegar / buscar / ordenar / alinear ----
    const alternar = useCallback((id) => { marcar(); setNodes(ns => alternarColapso(ns, id)); onDirty(); }, [marcar, onDirty]);
    const plegarCapas = (plegar) => { marcar(); setNodes(ns => (plegar ? plegarTodos(ns, true) : plegarTodos(ns, false, () => true))); onDirty(); setTimeout(() => fitView({ padding: .2, duration: 300 }), 100); };
    const irA = (id) => {
        const porId = mapa(refNodes.current), n = porId[id];
        if (!n) return;
        const plegados = ancestros(n, porId).filter(p => p.data && p.data.colapsado);
        setNodes(ns => { let r = ns; plegados.forEach(p => { r = alternarColapso(r, p.id); }); return r.map(m => ({ ...m, selected: m.id === id })); });
        setEdges(es => es.map(e => (e.selected ? { ...e, selected: false } : e)));
        setPanel("props"); setVerInsp(true);
        setTimeout(() => fitView({ nodes: [{ id }], duration: 400, maxZoom: 1.3, padding: .8 }), 150);
    };
    const resultados = useMemo(() => buscarNodos(nodosEstables, busqueda), [nodosEstables, busqueda]);
    const ordenarTodo = () => { marcar(); setNodes(ns => ordenarAuto(ns, refEdges.current)); onDirty(); setTimeout(() => fitView({ padding: .2, duration: 300 }), 150); };
    const ordenarContenido = () => { if (!selNodo || !esContenedor(selNodo)) return; marcar(); setNodes(ns => ordenarAuto(ns, refEdges.current, selNodo.id)); onDirty(); };
    const alinearSel = (modo) => { if (!modo) return; marcar(); setNodes(ns => alinear(ns, modo)); onDirty(); };

    // ---- copiar / pegar / duplicar ----
    const copiar = () => {
        const ns = refNodes.current, porId = mapa(ns), ids = new Set(ns.filter(n => n.selected).map(n => n.id));
        if (!ids.size) return false;
        ns.forEach(n => { if (ancestros(n, porId).some(p => ids.has(p.id))) ids.add(n.id); });
        PORTAPAPELES = {
            nodes: JSON.parse(JSON.stringify(ns.filter(n => ids.has(n.id)).map(({ measured, ...n }) => n))),
            edges: JSON.parse(JSON.stringify(refEdges.current.filter(e => ids.has(e.source) && ids.has(e.target)))),
        };
        PEGADOS = 0;
        return true;
    };
    const pegar = () => {
        if (!PORTAPAPELES || ro) return;
        marcar();
        PEGADOS++;
        const desp = 30 * PEGADOS, nuevoDe = {};
        PORTAPAPELES.nodes.forEach(n => { nuevoDe[n.id] = nuevoId("c"); });
        const existentes = new Set(refNodes.current.map(n => n.id));
        const nuevos = PORTAPAPELES.nodes.map(({ parentId, selected, ...n }) => {
            const copiadoPadre = parentId && nuevoDe[parentId];
            const conPadre = copiadoPadre || (parentId && existentes.has(parentId));
            return {
                ...JSON.parse(JSON.stringify(n)), id: nuevoDe[n.id], selected: !copiadoPadre,
                position: copiadoPadre ? n.position : { x: n.position.x + desp, y: n.position.y + desp },
                ...(conPadre ? { parentId: copiadoPadre || parentId } : {}),
            };
        });
        const aristas = PORTAPAPELES.edges.map(e => ({ ...e, id: nuevoId("e"), source: nuevoDe[e.source], target: nuevoDe[e.target], selected: false }));
        setNodes(ns => ordenar([...ns.map(n => (n.selected ? { ...n, selected: false } : n)), ...nuevos]));
        setEdges(es => [...es, ...aristas]);
        onDirty();
    };
    const duplicar = () => { if (!ro && copiar()) { PEGADOS = 0; pegar(); } };

    // ---- agregar una carpeta de Terraform al diagrama ----
    const [agregando, setAgregando] = useState(false);
    const agregarCarpeta = async (e) => {
        const r = await leerCarpetaTf(e.target.files);
        e.target.value = "";
        if (r.error) return window.alert(r.error);
        setAgregando(true);
        try {
            let d = await onAgregarCarpeta(r.archivos, aGrafo(refNodes.current, refEdges.current), false);
            const conflicto = d && d.ok && d.rows && d.rows[0] && d.rows[0].conflicto ? d.rows[0].capas : null;
            if (conflicto) {
                if (!window.confirm(`Ya existe(n) la(s) capa(s): ${conflicto.join(", ")}.\n¿Actualizarla(s) con el contenido de la carpeta? Se conservan las posiciones y descripciones de lo que sigue existiendo (puedes deshacer con Ctrl+Z).`)) return;
                d = await onAgregarCarpeta(r.archivos, aGrafo(refNodes.current, refEdges.current), true);
            }
            if (!d || !d.ok || !d.dataObj || !d.dataObj.grafo) return window.alert((d && d.msg) || "No se pudo agregar la carpeta.");
            marcar();
            const g = aEditor(d.dataObj.grafo);
            setNodes(g.nodes); setEdges(g.edges); onDirty();
            onAvisos(d.dataObj.avisos, `${d.dataObj.nodos} objetos y ${d.dataObj.aristas} conexiones agregados`);
            setTimeout(() => fitView({ padding: .2 }), 100);
        } catch (err) { window.alert("No se pudo agregar la carpeta."); }
        finally { setAgregando(false); }
    };

    // ---- historial y plantillas ----
    const cargarVersiones = useCallback(() => onVersiones(idDiagrama), [onVersiones, idDiagrama]);
    const cargarPrecios = useCallback(() => onPrecios(), [onPrecios]);
    const marcarFaltantes = (ids) => {
        const m = {}; ids.forEach(id => { m[id] = "falta"; });
        setMarcas(m);
        // si alguno esta plegado, se despliega para que se vea
        const porId = mapa(refNodes.current), plegados = new Set();
        ids.forEach(id => { const n = porId[id]; if (n) ancestros(n, porId).forEach(p => { if (p.data && p.data.colapsado) plegados.add(p.id); }); });
        if (plegados.size) setNodes(ns => { let r = ns; plegados.forEach(id => { r = alternarColapso(r, id); }); return r; });
    };
    // lo que existe en AWS y no estaba dibujado: se agrega debajo de lo existente (sin conectarlo con lo que ya estaba)
    const agregarFragmento = (grafoFrag) => {
        marcar();
        const r = colocarFragmento(refNodes.current, refEdges.current, grafoFrag);
        setNodes(r.nodes); setEdges(r.edges); onDirty();
        setTimeout(() => fitView({ padding: .2, duration: 300 }), 150);
    };
    const compararConCuenta = async (datos) => onCompararCuenta(await sobreCifrado(onClavePublica, datos), aGrafo(refNodes.current, refEdges.current));
    const restaurarVersion = async (v) => {
        if (!window.confirm(`¿Traer la versión ${v.version} (${new Date(v.created_at).toLocaleString()}) a la pantalla? Reemplaza lo que ves, sin guardar.`)) return;
        const d = await onVersionGet(v.id);
        const fila = d && d.rows && d.rows[0];
        if (!fila) return window.alert((d && d.msg) || "No se pudo leer esa versión.");
        marcar();
        const g = aEditor(fila.grafo);
        setNodes(g.nodes); setEdges(g.edges); onDirty();
        setTimeout(() => fitView({ padding: .2 }), 100);
    };
    const guardarComoPlantilla = async () => {
        const nom = window.prompt("Nombre de la plantilla de la empresa:", nombre);
        if (!nom || !nom.trim()) return;
        const desc = window.prompt("Descripción (opcional):", "") || "";
        const d = await onGuardarPlantilla(nom.trim(), desc, aGrafo(refNodes.current, refEdges.current));
        window.alert(d && d.ok ? "Plantilla guardada. La verás al crear un diagrama nuevo en cualquier proyecto." : ((d && d.msg) || "No se pudo guardar la plantilla."));
    };

    // ---- agregar una region/cuenta de AWS como una capa mas ----
    const agregarRegion = async (datos) => {
        setAgregandoCuenta(true);
        try {
            const sobre = await sobreCifrado(onClavePublica, datos);       // se reutiliza si hay que confirmar el reemplazo (credenciales ya vaciadas del formulario)
            let d = await onAgregarCuenta(sobre, aGrafo(refNodes.current, refEdges.current), false);
            const conflicto = d && d.ok && d.rows && d.rows[0] && d.rows[0].conflicto ? d.rows[0].capas : null;
            if (conflicto) {
                if (!window.confirm(`Esa región ya está en el diagrama (${conflicto.join(", ")}).\n¿Actualizarla con lo que hay hoy en AWS? Se conservan las posiciones, descripciones, comentarios y propiedades que editaste (puedes deshacer con Ctrl+Z).`)) return;
                d = await onAgregarCuenta(sobre, aGrafo(refNodes.current, refEdges.current), true);
            }
            if (!d || !d.ok || !d.dataObj || !d.dataObj.grafo) return window.alert((d && d.msg) || "No se pudo agregar la región.");
            marcar();
            const g = aEditor(d.dataObj.grafo);
            setNodes(g.nodes); setEdges(g.edges); onDirty();
            onAvisos(d.dataObj.avisos, `${d.dataObj.nodos} objetos y ${d.dataObj.aristas} conexiones agregados desde AWS`);
            setPanelCuenta(false);
            setTimeout(() => fitView({ padding: .2 }), 100);
        } catch (err) { window.alert("No se pudo agregar la región."); }
        finally { setAgregandoCuenta(false); }
    };

    // ---- exportar: imagen, Terraform, enlace ----
    const exportar = async (formato) => {
        const el = refLienzo.current && refLienzo.current.querySelector(".react-flow");
        if (!el || exportando) return;
        setExportando(true);
        const vp = getViewport();
        try {
            setNodes(ns => ns.map(n => (n.selected ? { ...n, selected: false } : n)));
            await fitView({ padding: .1, duration: 0 });
            await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
            const quitar = ["react-flow__minimap", "react-flow__controls", "react-flow__attribution", "react-flow__background", "react-flow__panel"];
            const opts = { backgroundColor: "#ffffff", pixelRatio: 2, filter: (n) => !(n.classList && quitar.some(c => n.classList.contains(c))) };
            const url = formato === "svg" ? await toSvg(el, opts) : await toPng(el, opts);
            descargar(url, `${slug(nombre) || "diagrama"}.${formato}`);
        } catch (err) {
            window.alert("No se pudo exportar la imagen: " + (err && err.message ? err.message : err));
        } finally {
            setViewport(vp, { duration: 0 });
            setExportando(false);
        }
    };
    const descargar = (url, archivo) => { const a = document.createElement("a"); a.href = url; a.download = archivo; document.body.appendChild(a); a.click(); a.remove(); };
    const bajarTerraform = () => {
        const r = generarTerraform(refNodes.current, nombre);
        if (!r.recursos) return window.alert("El diagrama no tiene objetos con equivalente en Terraform.");
        const url = URL.createObjectURL(new Blob([r.texto], { type: "text/plain;charset=utf-8" }));
        descargar(url, `${slug(nombre) || "diagrama"}.tf`);
        setTimeout(() => URL.revokeObjectURL(url), 2000);
        window.alert(`Se generó un esqueleto con ${r.recursos} recurso(s).${r.avisos.length ? "\n\n" + r.avisos.join("\n") : ""}\n\nFaltan argumentos obligatorios y conexiones: revísalo antes de usarlo.`);
    };
    const copiarEnlace = () => {
        const url = `${window.location.origin}/Aws/Diagrama?id=${idDiagrama}`;
        if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(() => window.alert("Enlace copiado:\n" + url), () => window.prompt("Copia el enlace:", url));
        else window.prompt("Copia el enlace:", url);
    };

    // Atajos: Ctrl+Z / Ctrl+Shift+Z / Ctrl+Y, Ctrl+C / V / D, Ctrl+S (no actuan mientras se escribe en un campo).
    acciones.current = { deshacer: () => !ro && deshacer(), rehacer: () => !ro && rehacer(), copiar, pegar, duplicar, guardar: () => { if (!guardando && !ro) onGuardar(nombre, aGrafo(refNodes.current, refEdges.current)); } };
    useEffect(() => {
        const alTeclear = (e) => {
            const t = e.target || {};
            if (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable || !(e.ctrlKey || e.metaKey)) return;
            const a = acciones.current, k = e.key.toLowerCase();
            if (k === "z") { e.preventDefault(); e.shiftKey ? a.rehacer() : a.deshacer(); }
            else if (k === "y") { e.preventDefault(); a.rehacer(); }
            else if (k === "c") a.copiar();
            else if (k === "v") a.pegar();
            else if (k === "d") { e.preventDefault(); a.duplicar(); }
            else if (k === "s") { e.preventDefault(); a.guardar(); }
        };
        window.addEventListener("keydown", alTeclear);
        return () => window.removeEventListener("keydown", alTeclear);
    }, []);

    const vista = useMemo(() => construirVista(nodes, edges, { selId: selNodo ? selNodo.id : null, resaltar, marcas }), [nodes, edges, selNodo, resaltar, marcas]);
    const hallazgos = useMemo(() => revisar(nodosEstables, aristasEstables), [nodosEstables, aristasEstables]);
    const acc = useMemo(() => ({ marcar, alternar, ro }), [marcar, alternar, ro]);
    const nCapas = nodes.filter(n => n.data && n.data.tipo === "capa").length;
    const btn = (activo) => "btn btn-sm " + (activo ? "btn-secondary" : "btn-outline-secondary");

    return (
        <AccionesCtx.Provider value={acc}>
        <style>{".react-flow__node.atenuado{opacity:.2;transition:opacity .15s} .react-flow__node.deriva-falta{outline:3px dashed #dc3545;outline-offset:3px;border-radius:8px}"}</style>
        <div className="d-flex flex-column" style={{ flex: 1, minHeight: 0, position: "relative" }}>
            {panelCuenta && !ro && (
                <div className="card shadow" style={{ position: "absolute", top: 52, right: 12, zIndex: 40, width: 360 }}>
                    <div className="card-header py-1 d-flex justify-content-between align-items-center"><b>Agregar región o cuenta de AWS</b><button className="btn btn-sm btn-link py-0" onClick={() => setPanelCuenta(false)}>Cerrar</button></div>
                    <div className="card-body p-2 small">
                        <div className="text-muted mb-2">Lee esa región en solo lectura y la agrega como una capa nueva. Sirve para sumar otra región o la cuenta de otro ambiente. Usa credenciales temporales: se cifran y no se guardan.</div>
                        <FormCuenta compacto onEnviar={agregarRegion} trabajando={agregandoCuenta} textoBoton="Agregar" />
                    </div>
                </div>)}
            <div className="d-flex flex-wrap gap-2 align-items-center p-2 border-bottom">
                {!ro && <button className={btn(verPaleta)} title="Mostrar/ocultar paleta de servicios" onClick={() => setVerPaleta(v => !v)}><i className="fa fa-th-list" /></button>}
                <input className="form-control form-control-sm" style={{ maxWidth: 260 }} value={nombre} maxLength={120} disabled={ro}
                    onChange={e => { setNombre(e.target.value); onDirty(); }} />
                {!ro && <button className="btn btn-primary btn-sm" disabled={guardando} onClick={() => onGuardar(nombre, aGrafo(nodes, edges))}>
                    <i className="fa fa-save me-1" />{guardando ? "Guardando…" : "Guardar"}</button>}
                {!ro && <div className="btn-group btn-group-sm">
                    <button className="btn btn-outline-secondary" title="Deshacer (Ctrl+Z)" disabled={!pasado.current.length} onClick={deshacer}><i className="fa fa-undo" /></button>
                    <button className="btn btn-outline-secondary" title="Rehacer (Ctrl+Shift+Z)" disabled={!futuro.current.length} onClick={rehacer}><i className="fa fa-redo" /></button>
                </div>}
                <div className="position-relative">
                    <input className="form-control form-control-sm" style={{ width: 190 }} placeholder="Buscar en el diagrama…" value={busqueda}
                        onChange={e => setBusqueda(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && resultados[0]) { irA(resultados[0].id); setBusqueda(""); } if (e.key === "Escape") setBusqueda(""); }} />
                    {resultados.length > 0 && <div className="list-group position-absolute shadow" style={{ zIndex: 20, width: 340, maxHeight: 320, overflowY: "auto", top: "100%" }}>
                        {resultados.map(r => (
                            <button key={r.id} type="button" className="list-group-item list-group-item-action py-1" style={{ fontSize: 12 }} onClick={() => { irA(r.id); setBusqueda(""); }}>
                                <b>{r.titulo}</b> <span className="text-muted">{r.sub}</span>{r.oculto && <span className="badge text-bg-secondary ms-1">plegado</span>}
                                {r.extra && <div className="text-muted" style={{ fontSize: 10 }}>{r.extra}</div>}
                            </button>))}
                    </div>}
                </div>
                <div className="btn-group btn-group-sm">
                    <button className="btn btn-outline-secondary" title="Ajustar la vista a todo el diagrama" onClick={() => fitView({ padding: .2 })}><i className="fa fa-expand" /></button>
                    {nCapas > 0 && <button className="btn btn-outline-secondary" title="Plegar todas las capas" onClick={() => plegarCapas(true)}><i className="fa fa-compress-alt" /></button>}
                    <button className="btn btn-outline-secondary" title="Desplegar todo" onClick={() => plegarCapas(false)}><i className="fa fa-expand-alt" /></button>
                    <button className={btn(resaltar)} title="Resaltar las conexiones del objeto seleccionado" onClick={() => setResaltar(v => !v)}><i className="fa fa-highlighter" /></button>
                    <button className={btn(leyenda)} title="Leyenda" onClick={() => setLeyenda(v => !v)}><i className="fa fa-info-circle" /></button>
                </div>
                {!ro && <div className="btn-group btn-group-sm">
                    <button className="btn btn-outline-secondary" title="Ordenar todo el diagrama automáticamente" onClick={ordenarTodo}><i className="fa fa-project-diagram me-1" />Ordenar</button>
                    <button className="btn btn-outline-secondary" title="Ordenar solo lo que contiene el contenedor seleccionado" disabled={!selNodo || !esContenedor(selNodo)} onClick={ordenarContenido}><i className="fa fa-sitemap" /></button>
                </div>}
                {!ro && <select className="form-select form-select-sm" style={{ width: 120 }} value="" onChange={e => alinearSel(e.target.value)} title="Alinear o distribuir los objetos seleccionados (Ctrl+clic para elegir varios)">
                    <option value="">Alinear…</option>
                    <option value="izq">Izquierda</option><option value="centroH">Centro (horizontal)</option><option value="der">Derecha</option>
                    <option value="arriba">Arriba</option><option value="centroV">Centro (vertical)</option><option value="abajo">Abajo</option>
                    <option value="distH">Distribuir horizontal</option><option value="distV">Distribuir vertical</option>
                </select>}
                {!ro && <label className={"btn btn-outline-secondary btn-sm mb-0" + (agregando ? " disabled" : "")} title="Agrega (o actualiza) una carpeta de Terraform como capa de este diagrama">
                    <i className="fa fa-folder-plus me-1" />{agregando ? "Agregando…" : "Agregar carpeta"}
                    <input type="file" hidden multiple disabled={agregando} onChange={agregarCarpeta} ref={refCarpeta} />
                </label>}
                {!ro && <button className={btn(panelCuenta)} title="Agrega otra región o cuenta de AWS como una capa más de este diagrama" onClick={() => setPanelCuenta(v => !v)}><i className="fab fa-aws me-1" />Agregar región</button>}
                <div className="btn-group btn-group-sm" title="Descargar">
                    <button className="btn btn-outline-secondary" disabled={exportando} onClick={() => exportar("png")}><i className="fa fa-image me-1" />PNG</button>
                    <button className="btn btn-outline-secondary" disabled={exportando} onClick={() => exportar("svg")}>SVG</button>
                    <button className="btn btn-outline-secondary" title="Descargar un esqueleto de Terraform del diagrama" onClick={bajarTerraform}><i className="fa fa-file-code me-1" />.tf</button>
                </div>
                <button className="btn btn-outline-secondary btn-sm" title="Copiar el enlace a este diagrama" onClick={copiarEnlace}><i className="fa fa-link" /></button>
                {!ro && <button className="btn btn-outline-secondary btn-sm" title="Guardar este diagrama como plantilla de la empresa" onClick={guardarComoPlantilla}><i className="fa fa-bookmark" /></button>}
                <button className={btn(ro)} title={ro ? "Solo lectura: pulsa para volver a editar" : "Pasar a solo lectura"} onClick={() => setRo(v => !v)}><i className={"fa " + (ro ? "fa-lock" : "fa-lock-open")} /></button>
                <button className={"ms-auto " + btn(verInsp)} title="Mostrar/ocultar panel derecho" onClick={() => setVerInsp(v => !v)}><i className="fa fa-sliders-h" /></button>
            </div>
            <div className="d-flex flex-grow-1" style={{ minHeight: 0 }}>
                {verPaleta && !ro && <Paleta />}
                <div ref={refLienzo} style={{ flex: 1 }} onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }} onDrop={onDrop}>
                    <ReactFlow nodes={vista.nodes} edges={vista.edges} nodeTypes={nodeTypes} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
                        onConnect={onConnect} onNodeDragStart={marcar} onNodeDragStop={onNodeDragStop} onBeforeDelete={async () => { if (ro) return false; marcar(); return true; }}
                        nodesDraggable={!ro} nodesConnectable={!ro} connectionMode={ConnectionMode.Loose} onlyRenderVisibleElements={nodes.length > 400}
                        defaultEdgeOptions={{ markerEnd: { type: MarkerType.ArrowClosed } }} deleteKeyCode={ro ? null : ["Backspace", "Delete"]} fitView>
                        <Background gap={16} /><Controls showInteractive={false} /><MiniMap pannable zoomable />
                        {leyenda && <Panel position="bottom-left"><div className="border rounded p-2 small shadow-sm" style={{ background: "var(--cui-body-bg, #fff)", maxWidth: 280 }}>
                            <div><svg width="40" height="8"><line x1="0" y1="4" x2="40" y2="4" stroke="#888" strokeWidth="2" /></svg> Conexión directa («A usa B»)</div>
                            <div><svg width="40" height="8"><line x1="0" y1="4" x2="40" y2="4" stroke="#888" strokeWidth="2" strokeDasharray="6 4" /></svg> Inferida (por nombre / estado remoto)</div>
                            <div><svg width="40" height="8"><line x1="0" y1="4" x2="40" y2="4" stroke="#0d6efd" strokeWidth="3" /></svg> Conexiones del objeto seleccionado</div>
                            <div><span style={{ outline: "2px dashed #dc3545", outlineOffset: 1, padding: "0 6px", marginRight: 6 }}>&nbsp;</span>Ya no existe en AWS (al comparar)</div>
                            {Object.entries(CAMBIOS).map(([k, [c, t]]) => <div key={k}><span className="badge me-1" style={{ background: c, color: "#fff", width: 70 }}>{t}</span>Plan de Terraform: {k}</div>)}
                            {Object.entries(TIPOS_CONEXION).map(([k, t]) => <div key={k}><svg width="40" height="8"><line x1="0" y1="4" x2="40" y2="4" stroke={t.stroke} strokeWidth={t.strokeWidth || 2} strokeDasharray={t.strokeDasharray} /></svg> {t.nombre}</div>)}
                            <div className="mt-1"><span style={{ border: "2px dashed #0d6efd", padding: "0 6px" }}>Capa</span> = una carpeta de Terraform. <i className="fa fa-chevron-down" /> pliega un contenedor; las flechas se agrupan con un número.</div>
                        </div></Panel>}
                    </ReactFlow>
                </div>
                {verInsp && <div style={{ width: 340, overflowY: "auto", borderLeft: "1px solid var(--cui-border-color, #dee2e6)" }}>
                    <ul className="nav nav-tabs nav-fill">
                        {[["props", "Props"], ["rev", "Revisión"], ["hist", "Historial"], ["costo", "Costo"], ["share", "Enlace"]].map(([k, t]) => (
                            <li className="nav-item" key={k}><button className={"nav-link py-1 px-1 " + (panel === k ? "active" : "")} style={{ fontSize: 11 }} onClick={() => setPanel(k)}>
                                {t} {k === "rev" && hallazgos.length > 0 && <span className={`badge text-bg-${hallazgos.some(h => h.nivel === "alto") ? "danger" : "warning"}`}>{hallazgos.length}</span>}</button></li>))}
                    </ul>
                    {panel === "hist" ? <PanelHistorial cargar={cargarVersiones} onRestaurar={restaurarVersion} />
                        : panel === "costo" ? <PanelCosto nodes={nodosEstables} cargarPrecios={cargarPrecios} onGuardar={onPrecioGuardar} onEliminar={onPrecioEli} />
                        : panel === "share" ? <PanelCompartir idDiagrama={idDiagrama} onCrear={onCompartirCrear} onListar={onCompartirList} onRevocar={onCompartirRevocar} />
                        : panel === "rev" ? <><PanelRevision hallazgos={hallazgos} onIr={irA} onCorregir={corregir} ro={ro} /><PanelDeriva onComparar={compararConCuenta} onIr={irA} ro={ro} onMarcar={marcarFaltantes} onQuitarMarcas={() => setMarcas({})} onAgregarFragmento={agregarFragmento} /></>
                        : <Inspector nodo={selNodo} multiples={selNodos.length > 1 ? selNodos : null} onMultiple={cambiarMuchos} arista={selArista} ro={ro} tieneHijos={tieneHijos} onNodo={cambiarNodo} onProps={cambiarProps} onContenedor={cambiarContenedor}
                            onArista={cambiarArista} onBorrar={borrarSel} onDuplicar={duplicar} onComentar={comentar} onQuitarComentario={quitarComentario} />}
                </div>}
            </div>
        </div>
        </AccionesCtx.Provider>
    );
};

// Vista de solo lectura para el enlace publico (sin sesion): navegar, plegar capas, buscar y resaltar conexiones.
const Lectura = ({ grafo }) => {
    const ini = useMemo(() => aEditor(grafo), [grafo]);
    const [nodes, setNodes] = useState(ini.nodes);
    const [busqueda, setBusqueda] = useState("");
    const { fitView } = useReactFlow();
    const onNodesChange = useCallback((c) => setNodes(ns => applyNodeChanges(c.filter(x => x.type === "select"), ns)), []);
    const alternar = useCallback((id) => setNodes(ns => alternarColapso(ns, id)), []);
    const acc = useMemo(() => ({ marcar: () => {}, alternar, ro: true }), [alternar]);
    const sel = nodes.find(n => n.selected);
    const vista = useMemo(() => construirVista(nodes, ini.edges, { selId: sel ? sel.id : null, resaltar: true }), [nodes, ini.edges, sel]);
    const resultados = useMemo(() => buscarNodos(nodes, busqueda), [nodes, busqueda]);
    useEffect(() => { setTimeout(() => fitView({ padding: .2 }), 80); }, [fitView]);
    const irA = (id) => {
        const porId = mapa(nodes), n = porId[id];
        if (!n) return;
        const plegados = ancestros(n, porId).filter(p => p.data && p.data.colapsado);
        setNodes(ns => { let r = ns; plegados.forEach(p => { r = alternarColapso(r, p.id); }); return r.map(m => ({ ...m, selected: m.id === id })); });
        setTimeout(() => fitView({ nodes: [{ id }], duration: 400, maxZoom: 1.3, padding: .8 }), 150);
    };
    return (
        <AccionesCtx.Provider value={acc}>
            <style>{".react-flow__node.atenuado{opacity:.2;transition:opacity .15s}"}</style>
            <div className="d-flex flex-column" style={{ flex: 1, minHeight: 0 }}>
                <div className="d-flex flex-wrap gap-2 align-items-center p-2 border-bottom">
                    <div className="position-relative">
                        <input className="form-control form-control-sm" style={{ width: 220 }} placeholder="Buscar en el diagrama…" value={busqueda} onChange={e => setBusqueda(e.target.value)} />
                        {resultados.length > 0 && <div className="list-group position-absolute shadow" style={{ zIndex: 20, width: 320, maxHeight: 300, overflowY: "auto", top: "100%" }}>
                            {resultados.map(r => <button key={r.id} type="button" className="list-group-item list-group-item-action py-1" style={{ fontSize: 12 }} onClick={() => { irA(r.id); setBusqueda(""); }}><b>{r.titulo}</b> <span className="text-muted">{r.sub}</span></button>)}
                        </div>}
                    </div>
                    <div className="btn-group btn-group-sm">
                        <button className="btn btn-outline-secondary" title="Ajustar la vista" onClick={() => fitView({ padding: .2 })}><i className="fa fa-expand" /></button>
                        <button className="btn btn-outline-secondary" title="Plegar las capas" onClick={() => setNodes(ns => plegarTodos(ns, true))}><i className="fa fa-compress-alt" /></button>
                        <button className="btn btn-outline-secondary" title="Desplegar todo" onClick={() => setNodes(ns => plegarTodos(ns, false, () => true))}><i className="fa fa-expand-alt" /></button>
                    </div>
                    <span className="text-muted small">Haz clic en un objeto para resaltar sus conexiones.</span>
                </div>
                <div style={{ flex: 1 }}>
                    <ReactFlow nodes={vista.nodes} edges={vista.edges} nodeTypes={nodeTypes} onNodesChange={onNodesChange} nodesDraggable={false} nodesConnectable={false}
                        connectionMode={ConnectionMode.Loose} defaultEdgeOptions={{ markerEnd: { type: MarkerType.ArrowClosed } }} deleteKeyCode={null} fitView>
                        <Background gap={16} /><Controls showInteractive={false} /><MiniMap pannable zoomable />
                    </ReactFlow>
                </div>
            </div>
        </AccionesCtx.Provider>
    );
};
export const LienzoLectura = (props) => <ReactFlowProvider><Lectura {...props} /></ReactFlowProvider>;

const Lienzo = (props) => <ReactFlowProvider><Editor {...props} /></ReactFlowProvider>;
export default Lienzo;
